import type { PatchFileRecordRegionPayload } from "@cohesive/api-client";
import { useCallback, useEffect, useRef } from "react";

export const AUTOSAVE_IDLE_MS = 5_000;

type PatchRegions = (payload: PatchFileRecordRegionPayload) => Promise<unknown>;

export interface HtmlIslandAutosaveState {
  pendingCount: number;
  isSaving: boolean;
}

export interface UseHtmlIslandAutosaveParams {
  /**
   * Read an island's current innerHTML by edit id. The detail pane reads the
   * live iframe DOM (same-origin); the canvas block editor reads a store fed
   * by cohesive-block runtime messages (opaque-origin iframe). Return null
   * when the island no longer exists.
   */
  readIslandContent: (editId: string) => string | null;
  patchRegions: PatchRegions;
  /** Called after a successful PATCH with exactly the regions saved — the
   *  canvas editor merges these into its locally-held original body. */
  onSaveSuccess: (
    savedRegions: PatchFileRecordRegionPayload["regions"],
  ) => void;
  /**
   * Normalization applied uniformly to every island string entering the hook
   * (baselines, dirty-compares, payloads). Used to reverse presigned URL
   * rewrites so saves persist stable public URLs. Applying it anywhere less
   * than everywhere would make post-save baselines diverge from reads and
   * loop the saver.
   */
  sanitizeContent?: (html: string) => string;
  /** Save-state sink ({pendingCount, isSaving}); emitted with zeros on
   *  reset/unmount. */
  onAutosaveState?: (state: HtmlIslandAutosaveState) => void;
}

export interface UseHtmlIslandAutosaveResult {
  /** Region ids with local content differing from baseline. */
  getDirtyEditIds: () => ReadonlySet<string>;
  handleIslandFocus: (editId: string, innerHTML: string) => void;
  handleIslandInput: (editId: string, innerHTML: string) => void;
  handleIslandFocusOut: (editId: string, innerHTML: string) => void;
  flushDirtyRegions: () => void;
  /**
   * Clear autosave state; pass `{ flush: true }` to PATCH pending regions
   * first. A flushed PATCH keeps reporting `isSaving` until it settles, so
   * publish stays gated until the flushed regions have landed.
   */
  resetAutosave: (options?: { flush?: boolean }) => void;
}

export function useHtmlIslandAutosave({
  readIslandContent,
  patchRegions,
  onSaveSuccess,
  sanitizeContent,
  onAutosaveState,
}: UseHtmlIslandAutosaveParams): UseHtmlIslandAutosaveResult {
  const baselineByIdRef = useRef(new Map<string, string>());
  const dirtyIdsRef = useRef(new Set<string>());
  /** Mutex for batch-save coalescing — not the source of `isSaving`. */
  const saveInFlightRef = useRef(false);
  const flushAfterFlightRef = useRef(false);
  /**
   * Region PATCHes currently in flight, counting both batch saves and the
   * detached flushes fired on iframe reload / pane teardown. `isSaving`
   * derives from this so the publish gate stays closed until every write has
   * landed, including flushes whose autosave state was already cleared.
   */
  const inFlightSaveCountRef = useRef(0);
  /**
   * Bumped by every `resetAutosave`. A PATCH that settles after its
   * generation was retired must not write baselines or re-dirty regions: the
   * document it was collected from is gone.
   */
  const generationRef = useRef(0);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const patchRegionsRef = useRef(patchRegions);
  const onSaveSuccessRef = useRef(onSaveSuccess);
  const readIslandContentRef = useRef(readIslandContent);
  const sanitizeContentRef = useRef(sanitizeContent);
  const onAutosaveStateRef = useRef(onAutosaveState);

  useEffect(() => {
    patchRegionsRef.current = patchRegions;
  }, [patchRegions]);
  useEffect(() => {
    onSaveSuccessRef.current = onSaveSuccess;
  }, [onSaveSuccess]);
  useEffect(() => {
    readIslandContentRef.current = readIslandContent;
  }, [readIslandContent]);
  useEffect(() => {
    sanitizeContentRef.current = sanitizeContent;
  }, [sanitizeContent]);
  useEffect(() => {
    onAutosaveStateRef.current = onAutosaveState;
  }, [onAutosaveState]);

  const sanitize = useCallback((html: string): string => {
    const sanitizer = sanitizeContentRef.current;
    return sanitizer ? sanitizer(html) : html;
  }, []);

  const syncAutosaveState = useCallback(() => {
    onAutosaveStateRef.current?.({
      pendingCount: dirtyIdsRef.current.size,
      isSaving: inFlightSaveCountRef.current > 0,
    });
  }, []);

  const setRegionDirty = useCallback(
    (editId: string, sanitizedHTML: string) => {
      const baseline = baselineByIdRef.current.get(editId);
      if (baseline === undefined) {
        baselineByIdRef.current.set(editId, sanitizedHTML);
        dirtyIdsRef.current.delete(editId);
      } else if (sanitizedHTML === baseline) {
        dirtyIdsRef.current.delete(editId);
      } else {
        dirtyIdsRef.current.add(editId);
      }
      syncAutosaveState();
    },
    [syncAutosaveState],
  );

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current !== null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const readSanitizedIslandContent = useCallback(
    (editId: string): string | null => {
      const content = readIslandContentRef.current(editId);
      return content === null ? null : sanitize(content);
    },
    [sanitize],
  );

  const collectDirtyRegionsPayload =
    useCallback((): PatchFileRecordRegionPayload["regions"] => {
      const regions: PatchFileRecordRegionPayload["regions"] = [];
      for (const editId of dirtyIdsRef.current) {
        const content = readSanitizedIslandContent(editId);
        if (content === null) {
          dirtyIdsRef.current.delete(editId);
          continue;
        }
        const baseline = baselineByIdRef.current.get(editId);
        if (baseline === undefined || content === baseline) {
          dirtyIdsRef.current.delete(editId);
          continue;
        }
        regions.push({ region_id: editId, content });
      }
      return regions;
    }, [readSanitizedIslandContent]);

  const runBatchSave = useCallback(() => {
    if (saveInFlightRef.current) {
      flushAfterFlightRef.current = true;
      return;
    }

    const regions = collectDirtyRegionsPayload();
    if (regions.length === 0) {
      syncAutosaveState();
      return;
    }

    const generation = generationRef.current;
    saveInFlightRef.current = true;
    inFlightSaveCountRef.current += 1;
    syncAutosaveState();

    void (async () => {
      try {
        await patchRegionsRef.current({ regions });
        if (generationRef.current === generation) {
          for (const { region_id, content } of regions) {
            baselineByIdRef.current.set(region_id, content);
            const currentContent = readSanitizedIslandContent(region_id);
            if (currentContent !== null && currentContent !== content) {
              // Edits during the in-flight PATCH must stay dirty for a follow-up save.
              dirtyIdsRef.current.add(region_id);
            } else {
              dirtyIdsRef.current.delete(region_id);
            }
          }
          onSaveSuccessRef.current(regions);
        }
      } catch (err) {
        console.error("[html-island-autosave] save failed:", err);
      } finally {
        inFlightSaveCountRef.current -= 1;
        if (generationRef.current === generation) {
          saveInFlightRef.current = false;
          const needsFollowUp = flushAfterFlightRef.current;
          flushAfterFlightRef.current = false;
          if (needsFollowUp) {
            runBatchSave();
          } else {
            syncAutosaveState();
          }
        } else {
          // The mutex and follow-up flag now belong to a newer generation.
          syncAutosaveState();
        }
      }
    })();
  }, [
    collectDirtyRegionsPayload,
    readSanitizedIslandContent,
    syncAutosaveState,
  ]);

  const flushDirtyRegions = useCallback(() => {
    clearIdleTimer();
    runBatchSave();
  }, [clearIdleTimer, runBatchSave]);

  const scheduleIdleFlush = useCallback(() => {
    clearIdleTimer();
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null;
      flushDirtyRegions();
    }, AUTOSAVE_IDLE_MS);
  }, [clearIdleTimer, flushDirtyRegions]);

  const handleIslandFocus = useCallback(
    (editId: string, innerHTML: string) => {
      const sanitized = sanitize(innerHTML);
      if (!baselineByIdRef.current.has(editId)) {
        baselineByIdRef.current.set(editId, sanitized);
      }
      setRegionDirty(editId, sanitized);
    },
    [sanitize, setRegionDirty],
  );

  const handleIslandInput = useCallback(
    (editId: string, innerHTML: string) => {
      setRegionDirty(editId, sanitize(innerHTML));
      if (dirtyIdsRef.current.has(editId)) {
        scheduleIdleFlush();
      } else if (dirtyIdsRef.current.size === 0) {
        clearIdleTimer();
      }
    },
    [clearIdleTimer, sanitize, scheduleIdleFlush, setRegionDirty],
  );

  const handleIslandFocusOut = useCallback(
    (editId: string, innerHTML: string) => {
      setRegionDirty(editId, sanitize(innerHTML));
    },
    [sanitize, setRegionDirty],
  );

  const getDirtyEditIds = useCallback(() => new Set(dirtyIdsRef.current), []);

  const flushPendingRegions = useCallback(() => {
    clearIdleTimer();
    const regions = collectDirtyRegionsPayload();
    if (regions.length === 0) {
      return;
    }
    inFlightSaveCountRef.current += 1;
    void patchRegionsRef
      .current({ regions })
      .catch((err) => {
        console.error("[html-island-autosave] save failed:", err);
      })
      .finally(() => {
        inFlightSaveCountRef.current -= 1;
        syncAutosaveState();
      });
  }, [clearIdleTimer, collectDirtyRegionsPayload, syncAutosaveState]);

  const resetAutosave = useCallback(
    (options?: { flush?: boolean }) => {
      if (options?.flush) {
        flushPendingRegions();
      } else {
        clearIdleTimer();
      }
      generationRef.current += 1;
      baselineByIdRef.current.clear();
      dirtyIdsRef.current.clear();
      saveInFlightRef.current = false;
      flushAfterFlightRef.current = false;
      // Derive rather than hard-reset: PATCHes from the retired generation
      // may still be in flight and must keep reporting `isSaving`.
      syncAutosaveState();
    },
    [clearIdleTimer, flushPendingRegions, syncAutosaveState],
  );

  useEffect(() => {
    return () => {
      // Teardown: flush, then clear the consumer's state outright. The
      // surfaces that read it (indicator, publish gate) unmount with this
      // hook, so there is nothing left to gate once they are gone.
      resetAutosave({ flush: true });
      onAutosaveStateRef.current?.({ pendingCount: 0, isSaving: false });
    };
  }, [resetAutosave]);

  return {
    getDirtyEditIds,
    handleIslandFocus,
    handleIslandInput,
    handleIslandFocusOut,
    flushDirtyRegions,
    resetAutosave,
  };
}
